import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTenant } from '../tenant/TenantContext'
import { useI18n } from '../i18n'
import { apiGet } from '../utils/api'

type Stats = {
  endpoints_count: number
  targets_count: number
  policies_count: number
  schedules_count: number
  repositories_count: number
  jobs_last24_success: number
  jobs_last24_failed: number
  queued_count: number
  running_count: number
  recent_jobs: { id: number; endpoint_id: number; task_type: string; state: string; created_at: string }[]
  last_backup_per_endpoint: { endpoint: { id: number; name: string }, last_job?: { id: number; created_at: string; state: string; task_type: string } | null }[]
}

type Service = { name: string; ok: boolean; details?: string }

type BackupSizePoint = { date: string; avg_bytes: number | null; sample_count: number }
type BackupSizeAnomaly = {
  job_id: number
  endpoint_id: number
  target_id?: number | null
  target_name?: string | null
  bytes_processed?: number | null
  baseline_bytes?: number | null
  delta_bytes?: number | null
  percent_change?: number | null
  anomaly_percent?: number | null
  samples?: number | null
  created_at: string
}

export default function Home({ me }: { me: any }) {
  const { t } = useI18n()
  const { current } = useTenant()
  const tenantId = current?.id
  const [stats, setStats] = useState<Stats | null>(null)
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [series, setSeries] = useState<{date: string, success: number, failed: number, total?: number}[]>([])
  const [days, setDays] = useState<number>(7)
  const [endpoints, setEndpoints] = useState<{id: number, name: string}[]>([])
  const [endpointId, setEndpointId] = useState<number|''>('')
  const [taskType, setTaskType] = useState<string>('')
  const [sizeSeries, setSizeSeries] = useState<BackupSizePoint[]>([])
  const [sizeAnomalies, setSizeAnomalies] = useState<BackupSizeAnomaly[]>([])
  const latestAvgBytes = useMemo(() => {
    for (let i = sizeSeries.length - 1; i >= 0; i--) {
      const value = sizeSeries[i].avg_bytes
      if (value !== null && value !== undefined) return value
    }
    return null
  }, [sizeSeries])

  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        const s = await apiGet('/status'); setServices(s.services)
        if (tenantId) {
          const statsData = await apiGet(`/tenants/${tenantId}/stats`)
          setStats(statsData)
          const eps = await apiGet(`/tenants/${tenantId}/endpoints`)
          setEndpoints(eps)
          const params = new URLSearchParams()
          params.set('days', String(days))
          if (endpointId) params.set('endpoint_id', String(endpointId))
          if (taskType) params.set('task_type', taskType)
          const ts = await apiGet(`/tenants/${tenantId}/stats/timeseries?` + params.toString())
          setSeries(ts)
          const size = await apiGet(`/tenants/${tenantId}/stats/backup-size?` + params.toString())
          setSizeSeries(size?.series || [])
          setSizeAnomalies(size?.anomalies || [])
        }
        else {
          setSizeSeries([])
          setSizeAnomalies([])
        }
      } finally { setLoading(false) }
    })()
  }, [tenantId, days, endpointId, taskType])

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Welcome {me?.name || me?.email || ''}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card title="Endpoints" value={stats?.endpoints_count ?? '—'} />
        <Card title="Targets" value={stats?.targets_count ?? '—'} />
        <Card title="Policies" value={stats?.policies_count ?? '—'} />
        <Card title="Schedules" value={stats?.schedules_count ?? '—'} />
        <Card title="Repositories" value={stats?.repositories_count ?? '—'} />
        <Card title="Queued" value={stats?.queued_count ?? '—'} />
        <Card title="Running" value={stats?.running_count ?? '—'} />
        <Card title="Success (24h)" value={stats?.jobs_last24_success ?? '—'} />
        <Card title="Failed (24h)" value={stats?.jobs_last24_failed ?? '—'} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card">
          <h3 className="font-semibold mb-2">Service Health</h3>
          {loading ? <p>Loading…</p> : (
            <ul className="space-y-1">
              {services.map(s => (
                <li key={s.name}><span className={s.ok ? 'text-green-600' : 'text-red-600'}>●</span> {s.name}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="card">
          <h3 className="font-semibold mb-2">Recent Jobs</h3>
          <ul className="text-sm space-y-1">
            {stats?.recent_jobs?.map(j => (
              <li key={j.id}>
                <span className="text-gray-600 dark:text-gray-400 mr-1">#{j.id}</span>
                <span className="mr-2">{j.task_type}</span>
                <span className={(() => {
                  const base = 'px-2 py-0.5 rounded text-xs font-medium mr-2'
                  switch (j.state) {
                    case 'success': return base + ' bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    case 'failed': return base + ' bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    case 'running': return base + ' bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                    case 'queued': return base + ' bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                    default: return base + ' bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }
                })()}>{j.state}</span>
                <span className="text-gray-600 dark:text-gray-400">{new Date(j.created_at).toLocaleString()}</span>
              </li>
            )) || <li>-</li>}
          </ul>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Jobs (timeseries)</h3>
          <div className="flex gap-2 items-center">
            <select className="input" value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={7}>7d</option>
              <option value={14}>14d</option>
              <option value={30}>30d</option>
              <option value={90}>90d</option>
            </select>
            <select className="input" value={endpointId} onChange={e => setEndpointId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">All endpoints</option>
              {endpoints.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <input className="input" placeholder="task type (opt)" value={taskType} onChange={e => setTaskType(e.target.value)} />
          </div>
        </div>
        <MiniBars data={series} />
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">{t('home.size.title')}</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {latestAvgBytes !== null ? formatBytes(latestAvgBytes) : t('home.size.no_data')}
          </span>
        </div>
        {sizeSeries.length ? <SizeChart data={sizeSeries} /> : <p className="text-sm text-gray-500">{t('home.size.no_data')}</p>}
        {sizeAnomalies.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-semibold mb-1">{t('home.size.anomalies')}</h4>
            <ul className="text-xs space-y-1">
              {sizeAnomalies.slice(0, 5).map(anomaly => {
                const percent = anomaly.anomaly_percent ?? anomaly.percent_change
                return (
                  <li key={anomaly.job_id} className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">#{anomaly.job_id}</span>
                    <span>{formatPercent(percent)}</span>
                    <span>{formatBytes(anomaly.bytes_processed)}</span>
                    {anomaly.target_name && <span className="text-gray-500">{anomaly.target_name}</span>}
                    <span className="text-gray-500">{new Date(anomaly.created_at).toLocaleString()}</span>
                    <Link className="link ml-auto" to={`/jobs/${anomaly.job_id}`}>{t('home.size.view_job')}</Link>
                  </li>
                )
              })}
            </ul>
            {sizeAnomalies.length > 5 && (
              <p className="text-xs text-gray-500 mt-1">{t('home.size.anomalies_more', { count: sizeAnomalies.length - 5 })}</p>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="font-semibold mb-2">Last Backup per Endpoint</h3>
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"><th className="text-left">Endpoint</th><th>Last</th><th>State</th></tr></thead>
          <tbody>
            {stats?.last_backup_per_endpoint?.map(x => (
              <tr key={x.endpoint.id} className="border-t border-gray-200 dark:border-gray-700">
                <td>{x.endpoint.name}</td>
                <td className="text-center">{x.last_job ? new Date(x.last_job.created_at).toLocaleString() : '—'}</td>
                <td className="text-center">{x.last_job?.state ? (
                  <span className={(() => {
                    const s = x.last_job!.state
                    const base = 'px-2 py-0.5 rounded text-xs font-medium'
                    switch (s) {
                      case 'success': return base + ' bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                      case 'failed': return base + ' bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                      case 'running': return base + ' bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                      case 'queued': return base + ' bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                      default: return base + ' bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                    }
                  })()}>{x.last_job!.state}</span>
                ) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Card({ title, value }: { title: string, value: any }) {
  return (
    <div className="card text-center">
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  )
}

function formatBytes(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-'
  const negative = value < 0
  let num = Math.abs(value)
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let idx = 0
  while (num >= 1024 && idx < units.length - 1) {
    num /= 1024
    idx += 1
  }
  const formatted = idx === 0 ? num.toFixed(0) : num.toFixed(2)
  return `${negative ? '-' : ''}${formatted} ${units[idx]}`
}

function formatPercent(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-'
  return `${value >= 0 ? '' : '-'}${Math.abs(value).toFixed(2)}%`
}

function SizeChart({ data }: { data: BackupSizePoint[] }) {
  const w = 500, h = 140, pad = 24
  const usable = data.filter(point => point.avg_bytes !== null && point.avg_bytes !== undefined)
  const max = Math.max(1, ...usable.map(d => Math.abs(d.avg_bytes ?? 0)))
  const bw = (w - pad * 2) / Math.max(1, data.length)
  return (
    <svg width={w} height={h} className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 rounded">
      {data.map((d, i) => {
        const value = d.avg_bytes ?? 0
        const height = Math.abs(value) / max * (h - pad * 2)
        const x = pad + i * bw + (bw * 0.1)
        const y = h - pad - height
        return (
          <g key={d.date}>
            <rect
              x={x}
              y={value >= 0 ? y : h - pad - height}
              width={bw * 0.8}
              height={height}
              fill="#4f46e5"
            />
            <text x={x} y={h - 6} fontSize={10} fill="currentColor">{d.date.slice(5)}</text>
            <title>{`${d.date}\nAvg: ${formatBytes(d.avg_bytes ?? 0)}\nSamples: ${d.sample_count}`}</title>
          </g>
        )
      })}
      {data.length > 1 && (
        <polyline
          fill="none"
          stroke="#4338ca"
          strokeWidth={1.5}
          points={data.map((d, i) => {
            const value = d.avg_bytes ?? 0
            const height = Math.abs(value) / max * (h - pad * 2)
            const x = pad + i * bw + (bw * 0.5)
            const y = value >= 0 ? (h - pad - height) : (h - pad)
            return `${x},${y}`
          }).join(' ')}
        />
      )}
    </svg>
  )
}

function MiniBars({ data }: { data: {date: string, success: number, failed: number, total?: number}[] }) {
  const w = 500, h = 140, pad = 24
  const max = Math.max(1, ...data.map(d => (d.total ?? (d.success + d.failed))))
  const bw = (w - pad*2) / Math.max(1, data.length)
  return (
    <svg width={w} height={h} className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 rounded">
      {data.map((d, i) => {
        const total = d.total ?? (d.success + d.failed)
        const height = total/max * (h - pad*2)
        const x = pad + i*bw
        const y = h - pad - height
        const succH = (d.success/Math.max(1,total)) * height
        const failH = (d.failed/Math.max(1,total)) * height
        return (
          <g key={d.date}>
            <rect x={x} y={y} width={bw*0.8} height={succH} fill="#16a34a" />
            <rect x={x} y={y+succH} width={bw*0.8} height={failH} fill="#dc2626" />
            <text x={x} y={h-6} fontSize={10} fill="currentColor">{d.date.slice(5)}</text>
            <title>{`${d.date}\nSuccess: ${d.success}\nFailed: ${d.failed}\nTotal: ${total}`}</title>
          </g>
        )
      })}
      {/* total line */}
      {data.length > 1 && (
        <polyline
          fill="none"
          stroke="#2563eb"
          strokeWidth={1.5}
          points={data.map((d,i) => {
            const total = d.total ?? (d.success + d.failed)
            const height = total/max * (h - pad*2)
            const x = pad + i*bw + (bw*0.4)
            const y = h - pad - height
            return `${x},${y}`
          }).join(' ')}
        />
      )}
    </svg>
  )
}
